/**
 * Utility for handling PostgreSQL numeric type charges from Golang backend
 * The backend returns pgtype.Numeric as JSON: { "Int": 8, "Exp": 2, "Status": 2, "NaN": false, "InfinityModifier": 0 }
 */

export interface PgNumeric {
  Int: {
    big: {
      words: number[];
      neg: boolean;
      abs: boolean;
    };
    overflow: boolean;
  };
  Exp: number;
  Status: number;
  NaN: boolean;
  InfinityModifier: number;
}

export interface SimpleCharge {
  amount: number;
  currency?: string;
}

/**
 * Convert pgtype.Numeric to a simple number
 * @param pgNumeric - The PostgreSQL numeric type from Golang
 * @returns The numeric value as a JavaScript number
 */
export function parsePgNumeric(pgNumeric: PgNumeric | null | undefined): number {
  if (!pgNumeric || pgNumeric.NaN) {
    return 0;
  }

  // Handle infinity cases
  if (pgNumeric.InfinityModifier !== 0) {
    return pgNumeric.InfinityModifier > 0 ? Infinity : -Infinity;
  }

  // Handle the Int field which contains the big.Int representation
  if (pgNumeric.Int && pgNumeric.Int.big && pgNumeric.Int.big.words) {
    // Convert the big.Int words to a number
    let value = 0;
    const { words } = pgNumeric.Int.big;
    const isNegative = pgNumeric.Int.big.neg;

    for (let i = 0; i < words.length; i += 1) {
      // Each word is a 32-bit uint, so multiply by 2^(32*i)
      value += words[i] * 2 ** (32 * i);
    }

    if (isNegative) {
      value = -value;
    }

    // Apply the exponent (Exp represents 10^Exp)
    const divisor = 10 ** Math.abs(pgNumeric.Exp);

    if (pgNumeric.Exp > 0) {
      return value * divisor;
    }
    return value / divisor;
  }

  return 0;
}

/**
 * Parse a charge from the API response
 * @param charge - The charge field from API response (could be PgNumeric or direct number)
 * @returns A simple charge object with amount
 */
export function parseCharge(charge: PgNumeric | number | null | undefined): SimpleCharge {
  if (typeof charge === 'number') {
    return { amount: charge };
  }

  const amount = parsePgNumeric(charge);
  return { amount };
}

/**
 * Format charge amount as currency string
 * @param charge - The charge from API response
 * @param currency - Currency symbol (default: '$')
 * @returns Formatted currency string
 */
export function formatCharge(
  charge: PgNumeric | number | null | undefined,
  currency = 'UGX ',
): string {
  const { amount } = parseCharge(charge);
  if (amount === 0) {
    return `${currency}0.00`;
  }
  return `${currency}${amount.toFixed(2)}`;
}

/**
 * Check if a charge is valid (non-zero and not NaN)
 * @param charge - The charge to validate
 * @returns True if charge is valid
 */
export function isValidCharge(charge: PgNumeric | number | null | undefined): boolean {
  if (typeof charge === 'number') {
    return charge !== 0 && !Number.isNaN(charge) && Number.isFinite(charge);
  }

  if (!charge || charge.NaN) {
    return false;
  }

  const amount = parsePgNumeric(charge);
  return amount !== 0 && !Number.isNaN(amount) && Number.isFinite(amount);
}

/**
 * Convert a number to pgtype.Numeric format for API requests
 * @param amount - The amount to convert
 * @returns PgNumeric compatible object
 */
export function toPgNumeric(amount: number): PgNumeric {
  // Convert the number to a string to extract the decimal part
  const str = amount.toString();
  const [integerPart, decimalPart = ''] = str.split('.');

  // Convert integer part to big.Int words
  const intValue = parseInt(integerPart.replace('-', ''), 10) || 0;
  const isNegative = str.startsWith('-');

  // Convert to 32-bit words (little-endian)
  const words: number[] = [];
  let remaining = intValue;

  while (remaining > 0) {
    words.push(remaining % 0x100000000);
    remaining = Math.floor(remaining / 0x100000000);
  }

  if (words.length === 0) {
    words.push(0);
  }

  return {
    Int: {
      big: {
        words,
        neg: isNegative,
        abs: !isNegative,
      },
      overflow: false,
    },
    Exp: decimalPart.length > 0 ? -decimalPart.length : 0,
    Status: 2, // 2 means the number is valid
    NaN: false,
    InfinityModifier: 0,
  };
}

export function parsePgNumericToString(num: PgNumeric): string {
  if (num.NaN) return 'NaN';
  if (num.InfinityModifier === 1) return 'Infinity';
  if (num.InfinityModifier === -1) return '-Infinity';
  if (num.Status !== 2) return ''; // not "Present"

  const intStr = num.Int.toString();

  if (num.Exp === 0) {
    return intStr;
  } if (num.Exp > 0) {
    // Multiply: append zeros
    return intStr + '0'.repeat(num.Exp);
  }
  // Divide: insert decimal point
  const exp = -num.Exp;
  if (intStr.length > exp) {
    const point = intStr.length - exp;
    return `${intStr.slice(0, point)}.${intStr.slice(point)}`;
  }
  return `0.${'0'.repeat(exp - intStr.length)}${intStr}`;
}
